import React from "react";
import { Routes, Route, Link, useParams } from "react-router-dom";
import {
  Row,
  Col,
  Card,
  CardBody,
  CardTitle,
  Badge,
  Button,
  Progress
} from "reactstrap";
import SuiteLayout from "../../_suite/SuiteLayout";
import SubscriptionPlgDashboard from "../../subscriptionPlg";
import { Colxx, Separator } from "Components/CustomBootstrap";
import BreadcrumbContainer from "Components/BreadcrumbContainer";
import IntlMessages from "Util/IntlMessages";
import {
  FUNNEL_COUNT_LABEL_CENTER,
  PLG_TRIAL_FUNNEL_OUTER_STYLES
} from "Constants/reactifyInlineStyles";
import { suiteApi } from "../../../../../api/suiteAdapters";
import { useAsyncResource } from "../../../../../hooks/useAsyncResource";
import { AsyncStateBoundary, SuitePageShell, KpiStatCards } from "../../_suite/primitives";

function TrialFunnelPage() {
  const { data, loading, error } = useAsyncResource(
    () => suiteApi.plg.listTrials(),
    []
  );
  const { data: funnel } = useAsyncResource(
    () => suiteApi.plg.getTrialFunnel(),
    []
  );
  const steps = [
    { label: "Signup", key: "signup" },
    { label: "Activation", key: "activation" },
    { label: "Paid convert", key: "convert" }
  ];
  return (
    <SuitePageShell heading={<IntlMessages id="suite.plg.trials" />}>
      <div className="d-flex justify-content-between align-items-end mb-4 px-2">
        {steps.map((s, i) => {
          const n = funnel
            ? funnel[s.key as keyof typeof funnel]
            : null;
          return (
            <div key={s.key} className="text-center flex-grow-1">
              <div
                className="bg-info mx-auto rounded position-relative"
                style={PLG_TRIAL_FUNNEL_OUTER_STYLES[i]}
              >
                {n != null && (
                  <span
                    className="position-absolute text-white small font-weight-bold"
                    style={FUNNEL_COUNT_LABEL_CENTER}
                  >
                    {n}
                  </span>
                )}
              </div>
              <div className="small mt-1">{s.label}</div>
            </div>
          );
        })}
      </div>
      <AsyncStateBoundary loading={loading} error={error}>
        {(data || []).map(t => (
          <Card key={t.id} className="mb-2 border-0 shadow-sm">
            <CardBody className="d-flex justify-content-between">
              <div>
                <strong>{t.company}</strong>
                <div className="small text-muted">Started {t.started}</div>
                {t.cohort && (
                  <Badge color="light" className="text-dark mt-1">
                    {t.cohort}
                  </Badge>
                )}
              </div>
              <div className="text-end">
                <Badge>{t.step}</Badge>
                <div className="small">Score {t.score}</div>
              </div>
            </CardBody>
          </Card>
        ))}
      </AsyncStateBoundary>
    </SuitePageShell>
  );
}

function Account360Page() {
  const { data, loading, error } = useAsyncResource(
    () => suiteApi.plg.listAccounts(),
    []
  );
  return (
    <SuitePageShell heading={<IntlMessages id="suite.plg.accounts" />}>
      <AsyncStateBoundary loading={loading} error={error}>
        {(data || []).map(a => (
          <Card key={a.id} className="mb-4 border-0 shadow-sm">
            <CardBody>
              <div className="d-flex flex-wrap justify-content-between mb-3">
                <h4 className="mb-0">{a.name}</h4>
                <Badge color={a.health === "good" ? "success" : "warning"}>
                  {a.health}
                </Badge>
              </div>
              <KpiStatCards
                items={[
                  { title: "MRR", value: `$${a.mrr}` },
                  { title: "Stage", value: a.stage },
                  {
                    title: "Expansion score",
                    value: a.expansionScore != null ? a.expansionScore : "—",
                    sub: a.seats != null ? `${a.seats} seats` : undefined
                  }
                ]}
              />
              <Button tag={Link} color="primary" size="sm" to={a.id} relative="path">
                Deep dive
              </Button>
            </CardBody>
          </Card>
        ))}
      </AsyncStateBoundary>
    </SuitePageShell>
  );
}

function AccountDetailPage() {
  const { id } = useParams();
  const { data, loading, error } = useAsyncResource(
    () => suiteApi.plg.getAccount(id),
    [id]
  );
  return (
    <div>
      <Row>
        <Colxx xxs="12">
          <BreadcrumbContainer
            heading={<IntlMessages id="suite.plg.account-detail" />}
          />
          <Separator className="mb-4" />
        </Colxx>
      </Row>
      <Button tag={Link} color="light" to=".." relative="path" className="mb-3">
        ← Back
      </Button>
      <AsyncStateBoundary loading={loading} error={error} empty={!data}>
        {data && (
          <Card>
            <CardBody>
              <h3>{data.name}</h3>
              <p>MRR ${data.mrr}</p>
              {data.expansionScore != null && (
                <p>
                  Expansion score:{" "}
                  <Badge color="info">{data.expansionScore}</Badge>
                </p>
              )}
              {data.seats != null && <p>Seats: {data.seats}</p>}
            </CardBody>
          </Card>
        )}
      </AsyncStateBoundary>
    </div>
  );
}

function FeatureAdoptionPage() {
  const { data, loading, error } = useAsyncResource(
    () => suiteApi.plg.listFeatures(),
    []
  );
  return (
    <SuitePageShell heading={<IntlMessages id="suite.plg.features" />}>
      <AsyncStateBoundary loading={loading} error={error}>
        {(data || []).map(f => (
          <Card key={f.id} className="mb-3 border-0 shadow-sm">
            <CardBody>
              <div className="d-flex justify-content-between mb-1">
                <strong>{f.name}</strong>
                <span>
                  {f.adoptionPct}% / {f.targetPct}%
                </span>
              </div>
              <Progress value={(f.adoptionPct / f.targetPct) * 100} />
            </CardBody>
          </Card>
        ))}
      </AsyncStateBoundary>
    </SuitePageShell>
  );
}

function ChurnSavePage() {
  const { data, loading, error } = useAsyncResource(
    () => suiteApi.plg.listChurn(),
    []
  );
  return (
    <SuitePageShell heading={<IntlMessages id="suite.plg.churn" />}>
      <AsyncStateBoundary loading={loading} error={error}>
        {(data || []).map(c => (
          <Card
            key={c.id}
            className={`mb-3 border-0 shadow-sm ${c.saved ? "border-success border" : ""}`}
          >
            <CardBody className="d-flex justify-content-between flex-wrap align-items-center">
              <div>
                <strong>{c.account}</strong>
                <div className="text-muted small">{c.reason}</div>
                <div>MRR lost ${c.mrrLost}</div>
                {c.savePlay && (
                  <div className="small text-muted mt-1">{c.savePlay}</div>
                )}
              </div>
              <Button color={c.saved ? "success" : "primary"} disabled={c.saved}>
                {c.saved ? "Saved" : "Launch save offer"}
              </Button>
            </CardBody>
          </Card>
        ))}
      </AsyncStateBoundary>
    </SuitePageShell>
  );
}

export function SubscriptionPlgSuiteSection() {
  return (
    <Routes>
      <Route element={<SuiteLayout vertical="subscription-plg" />}>
        <Route index element={<SubscriptionPlgDashboard />} />
        <Route path="accounts" element={<Account360Page />} />
        <Route path="accounts/:id" element={<AccountDetailPage />} />
        <Route path="trials" element={<TrialFunnelPage />} />
        <Route path="features" element={<FeatureAdoptionPage />} />
        <Route path="churn" element={<ChurnSavePage />} />
      </Route>
    </Routes>
  );
}
